import { useEffect, useState } from "react";
import type { Lang } from "../../lib/i18n";
import { pick } from "../../lib/i18n";
import { apiRequest } from "../../lib/api";
import { AUDIT_ACTIONS, relativeTime } from "../lib/format";

interface ActivityData {
  stats: { total: number; completed: number; last24hStarts: number; stale3d: number };
  audit: Array<{ id: string; timestamp: number; actor: string; action: string; target?: string }>;
  emails: Array<{ id: string; timestamp: number; template: string; status: string; to_email: string; error?: string }>;
}

/** Overview / home tab (kt) — live stats + recent admin activity + recent emails. */
export function OverviewTab({ lang }: { lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [data, setData] = useState<ActivityData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const res = await apiRequest<ActivityData>("/api/admin/activity", { method: "GET" });
      if (!cancelled) {
        setData(res.data);
        setLoading(false);
      }
    }
    load();
    const timer = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  if (loading) return <div className="kd-activity-feed"><div className="kd-skeleton-card" style={{ height: 220 }} /></div>;
  const s = data?.stats ?? { total: 0, completed: 0, last24hStarts: 0, stale3d: 0 };
  const auditList = Array.isArray(data?.audit) ? data!.audit : [];
  const emailList = Array.isArray(data?.emails) ? data!.emails : [];

  return (
    <div className="kd-activity-feed">
      <div className="kd-admin-stats kd-activity-stats">
        <div className="kd-admin-stat"><div className="kd-admin-stat-num">{s.total}</div><div className="kd-admin-stat-label">{t("إجمالي الموظفين", "Total Employees")}</div></div>
        <div className="kd-admin-stat"><div className="kd-admin-stat-num">{s.completed}</div><div className="kd-admin-stat-label">{t("أكملوا", "Completed")}</div></div>
        <div className="kd-admin-stat"><div className="kd-admin-stat-num">{s.last24hStarts}</div><div className="kd-admin-stat-label">{t("آخر 24 ساعة", "Last 24h")}</div></div>
        <div className="kd-admin-stat" style={s.stale3d > 0 ? { borderColor: "#f59e0b" } : undefined}>
          <div className="kd-admin-stat-num" style={s.stale3d > 0 ? { color: "#f59e0b" } : undefined}>{s.stale3d}</div>
          <div className="kd-admin-stat-label">{t("متوقفون > 3 أيام", "Stale > 3d")}</div>
        </div>
      </div>

      <div className="kd-activity-grid">
        <div className="kd-activity-col">
          <h3>{t("أحدث النشاطات الإدارية", "Latest admin activity")}</h3>
          <ul className="kd-activity-list">
            {auditList.length === 0 && <li className="kd-activity-empty">{t("لا يوجد نشاط حتى الآن.", "No activity yet.")}</li>}
            {auditList.map((a) => (
              <li className="kd-activity-item" key={a.id}>
                <span className="kd-activity-icon">{AUDIT_ACTIONS[a.action]?.icon || "•"}</span>
                <div className="kd-activity-body">
                  <div className="kd-activity-title"><strong>{a.actor}</strong><span className="kd-activity-action">{a.action.replace(/_/g, " ")}</span></div>
                  {a.target && <div className="kd-activity-target">{a.target}</div>}
                </div>
                <time className="kd-activity-time" title={new Date(a.timestamp).toLocaleString()}>{relativeTime(a.timestamp, lang)}</time>
              </li>
            ))}
          </ul>
        </div>
        <div className="kd-activity-col">
          <h3>{t("آخر الإيميلات المرسلة", "Recent emails sent")}</h3>
          <ul className="kd-activity-list">
            {emailList.length === 0 && <li className="kd-activity-empty">{t("لم تُرسل إيميلات بعد.", "No emails sent yet.")}</li>}
            {emailList.map((m) => (
              <li className="kd-activity-item" key={m.id}>
                <span className="kd-activity-icon">{m.status === "sent" ? "✉️" : m.status === "error" ? "❌" : "⏸"}</span>
                <div className="kd-activity-body">
                  <div className="kd-activity-title"><strong>{m.template}</strong><span className={`kd-activity-badge kd-activity-badge-${m.status}`}>{m.status}</span></div>
                  <div className="kd-activity-target">→ {m.to_email}</div>
                  {m.error && <div className="kd-activity-error" title={m.error}>{m.error.slice(0, 80)}{m.error.length > 80 ? "…" : ""}</div>}
                </div>
                <time className="kd-activity-time" title={new Date(m.timestamp).toLocaleString()}>{relativeTime(m.timestamp, lang)}</time>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
